<script setup lang="ts">
import {
  useApplyCouponMutation,
  useCartCouponsQuery,
  useCartQuery,
  useHighlightedPromoCoupon,
  useRemoveCouponMutation,
  useShippingMethodsQuery,
} from "~~/app/composables/queries/useStoreApi";
import { useAnalytics } from "~~/app/composables/useAnalytics";
import InpostPayButton from "~~/app/components/inpostpay/InpostPayButton.vue";
import { formatCouponReduction } from "~~/shared/utils/couponLabel";

const { hideCtaOnMobile = false } = defineProps<{
  hideCtaOnMobile?: boolean;
}>();

const { formatPrice } = useMoney();
const brand = useBrand();
const { data: cart } = useCartQuery();
const { data: coupons } = useCartCouponsQuery();
const highlightedCoupon = useHighlightedPromoCoupon();
const couponInput = ref("");
const applyCoupon = useApplyCouponMutation();
const removeCoupon = useRemoveCouponMutation();
const analytics = useAnalytics();

// Drawer „Bezpłatny zwrot do 14 dni" — otwierany kliknięciem w notkę o zwrotach.
const showReturns = ref(false);

// PrestaShop pozwala mieć wiele aktywnych cart rule naraz — renderujemy wszystkie.
const appliedCoupons = computed(() => coupons.value?.applied ?? []);

// Kody do wiersza „Oszczędzasz" — tylko kupony z rabatem kwotowym/procentowym
// (kupon „tylko darmowa dostawa" nie dokłada do kwoty, więc go tu nie listujemy).
const appliedCodesText = computed(() =>
  appliedCoupons.value
    .filter(
      (coupon) => coupon.reductionPercent > 0 || coupon.reductionAmount > 0,
    )
    .map((coupon) => coupon.code)
    .join(", "),
);

// Dostawa w koszyku/drawerze: realny koszt zależy od strefy/adresu, którego na tym
// etapie jeszcze nie ma, więc pokazujemy najtańszą stawkę jako szacunek („od X”),
// albo „Gratis”, gdy spełnione są warunki darmowej dostawy (jak w checkout).
const { data: shippingMethods } = useShippingMethodsQuery({
  enabled: computed(() => (cart.value?.lines?.length ?? 0) > 0),
});

const cheapestShipping = computed(() => {
  const prices = (shippingMethods.value ?? [])
    .map((m) => m.price ?? 0)
    .filter((p) => p > 0);
  return prices.length ? Math.min(...prices) : null;
});

// Etykieta dla przypadku PŁATNEJ dostawy (gdy nie ma darmowej — patrz freeShipping):
// konkretny koszt po wyborze przewoźnika albo szacunek „od {najtańsza stawka}".
const shippingLabel = computed(() => {
  const cost = cart.value?.shippingCost ?? 0;
  if (cost > 0) return formatPrice(cost); // przewoźnik już wybrany
  if (cheapestShipping.value != null)
    return `od ${formatPrice(cheapestShipping.value)}`;
  return "od 8,99 zł"; // fallback, gdy API nie zwróciło stawek
});

const availableCoupon = computed(() => {
  const coupon = highlightedCoupon.value;
  if (!coupon) return null;
  if (
    appliedCoupons.value.some(
      (applied) => applied.idCartRule === coupon.idCartRule,
    )
  )
    return null;
  return coupon;
});

const availableReductionLabel = computed(() =>
  availableCoupon.value ? formatCouponReduction(availableCoupon.value) : "",
);

function applyAvailable() {
  const coupon = availableCoupon.value;
  if (!coupon || cartIsEmpty.value) return;
  applyCoupon.mutate(coupon.code, {
    onSuccess: () => {
      analytics.trackSelectPromotion(coupon.code, {
        locationId: "cart_summary_suggested",
      });
      couponInput.value = "";
    },
  });
}

// Autorytatywna jest sumaryczna wartość rabatu z koszyka (PS liczy ją po wszystkich
// regułach). Fallback (gdy koszyk nie zwrócił discountValue): suma kwotowych rabatów
// z kuponów. Kupon „tylko darmowa dostawa" nie dokłada tu nic (oszczędność = wysyłka).
const discountAmount = computed(() => {
  const fromCart = cart.value?.discountValue ?? 0;
  if (fromCart > 0) return fromCart;
  return appliedCoupons.value.reduce(
    (sum, coupon) => sum + (coupon.reductionAmount || 0),
    0,
  );
});

function submitCoupon() {
  const code = couponInput.value.trim();
  if (!code || cartIsEmpty.value) return;
  applyCoupon.mutate(code, {
    onSuccess: () => {
      analytics.trackSelectPromotion(code, {
        locationId: "cart_summary",
      });
      couponInput.value = "";
    },
  });
}

// Stałe kody rabatowe zależne od wartości zamówienia (lista w PromoStandingCodes).
// Klik w kod = wpisz go w pole i zastosuj (PrestaShop odrzuci poniżej progu kwotowego).
function applyStandingCode(code: string) {
  if (cartIsEmpty.value || applyCoupon.isPending.value) return;
  couponInput.value = code;
  submitCoupon();
}

const cartIsEmpty = computed(() => (cart.value?.lines?.length ?? 0) === 0);

// Koszyk kwalifikuje się do darmowej dostawy. Baza = produkty PO rabatach (jak PS:
// total z rabatami) — bez odjęcia discountValue voucher dawałby false-positive
// („Gratis" w koszyku, opłata w kasie). Spójne z paskiem postępu na stronie koszyka.
// Realna cena ustala się w kasie (autorytatywna).
const qualifiesForFreeShipping = computed(
  () =>
    (cart.value?.subtotal ?? 0) - (cart.value?.discountValue ?? 0) >=
    brand.freeShippingThreshold,
);

// „Gratis" gdy koszyk przekroczy próg darmowej dostawy albo gdy zastosowano kupon na
// darmową wysyłkę (free-shipping voucher daje darmową dostawę także poniżej progu).
const freeShipping = computed(
  () =>
    qualifiesForFreeShipping.value ||
    appliedCoupons.value.some((coupon) => coupon.freeShipping),
);

const couponErrorMessage = computed(() => {
  const err = applyCoupon.error.value as {
    statusMessage?: string;
    data?: { statusMessage?: string };
  } | null;
  return (
    err?.statusMessage ??
    err?.data?.statusMessage ??
    "Nieprawidłowy lub nieaktywny kod rabatowy."
  );
});
</script>

<template>
  <aside class="cart-summary">
    <h2 class="cart-summary__title">Wartość zamówienia</h2>

    <!-- Price breakdown -->
    <div class="cart-summary__rows">
      <div class="cart-summary__row">
        <span class="cart-summary__label">Cena produktów</span>
        <span class="cart-summary__value">{{
          formatPrice(cart?.subtotal ?? 0)
        }}</span>
      </div>
      <div
        v-if="discountAmount > 0"
        class="cart-summary__row cart-summary__row--discount"
      >
        <span class="cart-summary__label">{{ appliedCodesText }}</span>
        <span class="cart-summary__value cart-summary__value--discount"
          >Oszczędzasz: {{ formatPrice(discountAmount) }}</span
        >
      </div>
      <div class="cart-summary__row">
        <span class="cart-summary__label">Dostawa</span>
        <span
          class="cart-summary__value"
          :class="{ 'cart-summary__value--free': freeShipping }"
        >
          <template v-if="freeShipping">Gratis</template>
          <template v-else>{{ shippingLabel }}</template>
        </span>
      </div>
    </div>

    <div class="cart-summary__total-row">
      <span class="cart-summary__total-label"
        >Suma<br /><small>z VAT</small></span
      >
      <span class="cart-summary__total-value">{{
        formatPrice(cart?.total ?? 0)
      }}</span>
    </div>

    <!-- Coupon -->
    <div class="cart-summary__coupon-wrap">
      <button
        v-if="availableCoupon"
        type="button"
        class="cart-summary__available-coupon"
        :disabled="applyCoupon.isPending.value || cartIsEmpty"
        :aria-label="`Zastosuj kod rabatowy ${availableCoupon.code}`"
        @click="applyAvailable"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          aria-hidden="true"
        >
          <path
            d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"
          />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
        <span
          ><strong>{{ availableReductionLabel }}</strong> z kodem:
          <strong class="cart-summary__available-code">{{
            availableCoupon.code
          }}</strong></span
        >
      </button>
      <form
        class="cart-summary__coupon-form"
        :class="{
          'cart-summary__coupon-form--error': applyCoupon.isError.value,
        }"
        @submit.prevent="submitCoupon"
      >
        <input
          v-model="couponInput"
          type="text"
          class="field cart-summary__coupon-input"
          placeholder="Wpisz kod rabatowy"
          :disabled="applyCoupon.isPending.value || cartIsEmpty"
          @input="applyCoupon.reset()"
        />
        <button
          type="submit"
          class="cart-summary__coupon-btn"
          :disabled="applyCoupon.isPending.value || cartIsEmpty"
        >
          Dodaj
        </button>
      </form>
      <p v-if="cartIsEmpty" class="cart-summary__coupon-hint">
        Dodaj produkt do koszyka, aby zastosować kod rabatowy.
      </p>
      <p
        v-else-if="applyCoupon.isError.value"
        class="cart-summary__coupon-error"
      >
        {{ couponErrorMessage }}
      </p>

      <!-- Stałe kody rabatowe zależne od wartości zamówienia (klik = zastosuj) -->
      <PromoStandingCodes
        class="cart-summary__standing-codes"
        :disabled="applyCoupon.isPending.value || cartIsEmpty"
        @select="applyStandingCode"
      />
    </div>

    <!-- Applied coupon badges (PrestaShop dopuszcza wiele aktywnych reguł) -->
    <div v-if="appliedCoupons.length" class="cart-summary__applied-wrap">
      <p class="cart-summary__applied-label">Zastosowane rabaty</p>
      <div
        v-for="coupon in appliedCoupons"
        :key="coupon.idCartRule || coupon.code"
        class="cart-summary__applied-coupon"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#16a34a"
          stroke-width="2"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <span class="cart-summary__coupon-code">{{ coupon.code }}</span>
        <span class="cart-summary__coupon-pct">{{
          formatCouponReduction(coupon)
        }}</span>
        <button
          type="button"
          class="cart-summary__coupon-remove"
          :aria-label="`Usuń kupon ${coupon.code}`"
          @click="
            removeCoupon.mutate({
              id_cart_rule: coupon.idCartRule,
              code: coupon.code,
            })
          "
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- CTA -->
    <NuxtLink
      to="/checkout"
      class="cart-summary__cta"
      :class="{ 'cart-summary__cta--hide-mobile': hideCtaOnMobile }"
    >
      Przejdź do kasy
    </NuxtLink>

    <!-- InPost Pay — pokazywany na desktopie; na mobile widoczny w CartCheckoutFloat
         (dlatego chowamy go tu na tej samej szerokości co CTA, by nie dublować). -->
    <div
      class="cart-summary__inpost"
      :class="{ 'cart-summary__inpost--hide-mobile': hideCtaOnMobile }"
    >
      <ClientOnly>
        <InpostPayButton context="cart" />
      </ClientOnly>
    </div>

    <!-- Return note — otwiera drawer z instrukcją zwrotu -->
    <button
      type="button"
      class="cart-summary__return-note"
      @click="showReturns = true"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path d="M19 12H5M5 12l7-7M5 12l7 7" />
      </svg>
      Bezpłatny zwrot do 14 dni
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    </button>

    <ReturnsInfoDrawer :is-open="showReturns" @close="showReturns = false" />
  </aside>
</template>

<style scoped>
.cart-summary {
  background: var(--color-white);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cart-summary__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

/* Rows */
.cart-summary__rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cart-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cart-summary__label {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
}

.cart-summary__value {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
}

.cart-summary__row--discount .cart-summary__label {
  color: #16a34a;
}

.cart-summary__value--discount {
  color: #16a34a;
}

/* Darmowa dostawa — „Gratis" na zielono (ta sama zieleń co rabat na panelu) */
.cart-summary__value--free {
  color: #16a34a;
  font-weight: var(--font-weight-normal);
}

/* Total */
.cart-summary__total-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid var(--color-border);
}

.cart-summary__total-label {
  font-size: 1.125rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.2;
}

.cart-summary__total-label small {
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  display: block;
}

.cart-summary__total-value {
  font-size: 1.25rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

/* Coupon */
.cart-summary__coupon-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cart-summary__available-coupon {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  padding: 10px 16px;
  background: var(--promo-pill-bg);
  border: var(--promo-pill-border);
  border-radius: 55px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--color-black, #151515);
  line-height: 1;
  cursor: pointer;
  text-align: left;
  transition: opacity var(--transition-fast);
}

.cart-summary__available-coupon:hover:not(:disabled) {
  opacity: 0.8;
}

.cart-summary__available-coupon:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.cart-summary__available-coupon strong {
  font-weight: 700;
}

.cart-summary__available-code {
  color: var(--color-sale, #f83e3e);
  letter-spacing: 0.5px;
}

.cart-summary__coupon-form {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0;
  border: 1px solid var(--color-border-md);
  border-radius: var(--radius-full);
  overflow: hidden;
  transition: border-color var(--transition-fast);
}

.cart-summary__coupon-form--error {
  border-color: var(--color-error, #dc2626);
}

.cart-summary__coupon-error {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-error, #dc2626);
  line-height: 1.4;
}

.cart-summary__coupon-hint {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: #707070;
  line-height: 1.4;
}

/* Odstęp bloku kodów od pola kuponu (reszta stylów w PromoStandingCodes) */
.cart-summary__standing-codes {
  margin-top: 12px;
}

.cart-summary__coupon-input {
  border: none;
  background: transparent;
  padding: 0 12px;
  height: 44px;
  font-size: 0.875rem;
}

.cart-summary__coupon-input:focus {
  outline: none;
}

.cart-summary__coupon-btn {
  height: 44px;
  padding: 0 20px;
  background: var(--color-black);
  color: var(--color-white);
  border: none;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  cursor: pointer;
  border-radius: 0 var(--radius-full) var(--radius-full) 0;
  transition: opacity var(--transition-fast);
}

.cart-summary__coupon-btn:hover {
  opacity: 0.85;
}

/* Applied coupon */
.cart-summary__applied-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cart-summary__applied-label {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
}

.cart-summary__applied-coupon {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.cart-summary__coupon-code {
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: #16a34a;
}

.cart-summary__coupon-pct {
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: #16a34a;
}

.cart-summary__coupon-remove {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-muted);
  padding: 0;
  display: flex;
}

/* CTA */
.cart-summary__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  background: var(--color-black);
  color: var(--color-white);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-normal);
  text-decoration: none;
  border-radius: 28px;
  transition: opacity var(--transition-fast);
}

[data-brand="youlook"] .cart-summary__cta {
  border-radius: 0;
}

.cart-summary__cta:hover {
  opacity: 0.85;
}

/* InPost Pay (desktop) */
.cart-summary__inpost {
  display: block;
  width: 100%;
}

@media (max-width: 960px) {
  .cart-summary__cta--hide-mobile {
    display: none;
  }

  .cart-summary__inpost--hide-mobile {
    display: none;
  }
}

/* Return note */
.cart-summary__return-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin: 0;
  padding: 4px 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
}
.cart-summary__return-note:hover {
  text-decoration: underline;
}
</style>
